import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../firebase/config';
import toast from 'react-hot-toast';

const PaperSubmission = () => {
  const [formData, setFormData] = useState({
    // Team Details
    teamName: '',
    teamLeadName: '',
    teamLeadEmail: '',
    teamLeadPhone: '',
    institution: '',
    city: '',
    state: '',
    category: '',
    
    // Paper Information
    paperTitle: '',
    abstract: '',
    field: '',
    otherField: '',
    keywords: '',
    paperType: '',
    
    // Authors
    numberOfAuthors: '1',
    authors: [{ name: '', email: '', affiliation: '', role: '' }],
    
    // Links
    githubLink: '',
    demoLink: '',
    driveLink: '',
    
    // Optional Info
    outputType: '',
    setupRequirements: '',
    presentationMode: '',
    teamBio: '',
    priorPublication: '',
    publicationLink: '',
    
    // Declarations
    originalityConfirmed: false,
    displayConsent: false
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const handleAuthorChange = (index, field, value) => {
    const newAuthors = [...formData.authors];
    newAuthors[index][field] = value;
    setFormData({ ...formData, authors: newAuthors });
  };

  const addAuthor = () => {
    setFormData({
      ...formData,
      authors: [...formData.authors, { name: '', email: '', affiliation: '', role: '' }]
    });
  };

  const removeAuthor = (index) => {
    const newAuthors = formData.authors.filter((_, i) => i !== index);
    setFormData({ ...formData, authors: newAuthors });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      const docRef = await addDoc(collection(db, 'paperSubmissions'), {
        ...formData,
        submittedAt: serverTimestamp(),
        eventName: 'AI Day @ Anurag University',
        eventDate: '2026-01-05'
      });
      
      console.log('Paper submission saved with ID:', docRef.id);
      toast.success('Paper submitted successfully! We will contact you soon.');
      
      // Reset form
      setFormData({
        teamName: '', teamLeadName: '', teamLeadEmail: '', teamLeadPhone: '',
        institution: '', city: '', state: '', category: '',
        paperTitle: '', abstract: '', field: '', otherField: '', keywords: '', paperType: '',
        numberOfAuthors: '1', authors: [{ name: '', email: '', affiliation: '', role: '' }],
        githubLink: '', demoLink: '', driveLink: '',
        outputType: '', setupRequirements: '', presentationMode: '', teamBio: '',
        priorPublication: '', publicationLink: '',
        originalityConfirmed: false, displayConsent: false
      });
    } catch (error) {
      console.error('Error submitting paper:', error);
      toast.error('Error submitting paper. Please try again.');
    }
  };

  return (
    <div className="min-h-screen bg-gray-50 pt-24 pb-12">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Header */}
        <div className="text-center mb-12">
          <Link to="/events/anurag-university" className="inline-flex items-center gap-2 text-crimson hover:text-black transition-colors mb-4">
            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
            </svg>
            Back to Event Page
          </Link>
          <h1 className="text-4xl md:text-5xl font-outfit font-800 text-black mb-4">
            Research Paper & Poster Submission
          </h1>
          <p className="text-lg font-outfit font-600 text-gray-600">
            AI Day @ Anurag University · 5 January 2026
          </p>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-8">
          {/* Section 1: Team & Contact */}
          <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-sm">
            <h2 className="text-2xl font-outfit font-800 text-black mb-6 flex items-center gap-2">
              <span className="text-crimson">01</span> Team & Contact Information
            </h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="md:col-span-2">
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Team Name <span className="text-crimson">*</span>
                </label>
                <input
                  type="text"
                  name="teamName"
                  value={formData.teamName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="Enter your team name"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Team Lead Name <span className="text-crimson">*</span>
                </label>
                <input
                  type="text"
                  name="teamLeadName"
                  value={formData.teamLeadName}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="Full name"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Team Lead Email <span className="text-crimson">*</span>
                </label>
                <input
                  type="email"
                  name="teamLeadEmail"
                  value={formData.teamLeadEmail}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="email@example.com"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Team Lead Phone <span className="text-crimson">*</span>
                </label>
                <input
                  type="tel"
                  name="teamLeadPhone"
                  value={formData.teamLeadPhone}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="+91 XXXXX XXXXX"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Institution / Organization <span className="text-crimson">*</span>
                </label>
                <input
                  type="text"
                  name="institution"
                  value={formData.institution}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="University or Company name"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  City <span className="text-crimson">*</span>
                </label>
                <input
                  type="text"
                  name="city"
                  value={formData.city}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="City"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  State <span className="text-crimson">*</span>
                </label>
                <input
                  type="text"
                  name="state"
                  value={formData.state}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="State"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Category <span className="text-crimson">*</span>
                </label>
                <select
                  name="category"
                  value={formData.category}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit bg-white"
                >
                  <option value="">Select category</option>
                  <option value="student">Student</option>
                  <option value="research-scholar">Research Scholar</option>
                  <option value="faculty">Faculty</option>
                  <option value="industry">Industry / Startup</option>
                </select>
              </div>
            </div>
          </div>

          {/* Section 2: Paper Information */}
          <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-sm">
            <h2 className="text-2xl font-outfit font-800 text-black mb-6 flex items-center gap-2">
              <span className="text-crimson">02</span> Paper Information
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Paper Title <span className="text-crimson">*</span>
                </label>
                <input
                  type="text"
                  name="paperTitle"
                  value={formData.paperTitle}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="Full title of your research paper"
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Abstract (150-300 words) <span className="text-crimson">*</span>
                </label>
                <textarea
                  name="abstract"
                  value={formData.abstract}
                  onChange={handleChange}
                  required
                  rows="6"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit resize-none"
                  placeholder="Provide a concise summary of your research..."
                />
                <p className="text-sm text-gray-500 mt-1 font-outfit">
                  Word count: {formData.abstract.split(' ').filter(word => word.length > 0).length}
                </p>
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                    Field / Track <span className="text-crimson">*</span>
                  </label>
                  <select
                    name="field"
                    value={formData.field}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit bg-white"
                  >
                    <option value="">Select field</option>
                    <option value="ai-ml">AI / ML</option>
                    <option value="computer-vision">Computer Vision</option>
                    <option value="nlp">NLP</option>
                    <option value="robotics">Robotics</option>
                    <option value="healthcare-ai">Healthcare AI</option>
                    <option value="generative-ai">Generative AI</option>
                    <option value="data-science">Data Science / Analytics</option>
                    <option value="other">Other</option>
                  </select>
                </div>

                {formData.field === 'other' && (
                  <div>
                    <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                      Specify Other Field <span className="text-crimson">*</span>
                    </label>
                    <input
                      type="text"
                      name="otherField"
                      value={formData.otherField}
                      onChange={handleChange}
                      required
                      className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                      placeholder="Enter field name"
                    />
                  </div>
                )}

                <div>
                  <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                    Type <span className="text-crimson">*</span>
                  </label>
                  <select
                    name="paperType"
                    value={formData.paperType}
                    onChange={handleChange}
                    required
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit bg-white"
                  >
                    <option value="">Select type</option>
                    <option value="research-paper">Research Paper</option>
                    <option value="poster">Poster Presentation</option>
                    <option value="case-study">Case Study / Application Paper</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Keywords (3-5 keywords) <span className="text-crimson">*</span>
                </label>
                <input
                  type="text"
                  name="keywords"
                  value={formData.keywords}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="e.g., Machine Learning, Computer Vision, Deep Learning"
                />
                <p className="text-sm text-gray-500 mt-1 font-outfit">
                  Separate keywords with commas
                </p>
              </div>
            </div>
          </div>

          {/* Section 3: Authors */}
          <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-sm">
            <h2 className="text-2xl font-outfit font-800 text-black mb-6 flex items-center gap-2">
              <span className="text-crimson">03</span> Authors
            </h2>
            
            <div className="mb-6">
              <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                Number of Authors <span className="text-crimson">*</span>
              </label>
              <input
                type="number"
                name="numberOfAuthors"
                value={formData.numberOfAuthors}
                onChange={handleChange}
                min="1"
                required
                className="w-full md:w-48 px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
              />
            </div>

            <div className="space-y-6">
              {formData.authors.map((author, index) => (
                <div key={index} className="border-2 border-gray-200 p-6 rounded-lg relative">
                  <div className="flex justify-between items-center mb-4">
                    <h3 className="text-lg font-outfit font-700 text-black">
                      Author {index + 1}
                    </h3>
                    {index > 0 && (
                      <button
                        type="button"
                        onClick={() => removeAuthor(index)}
                        className="text-crimson hover:text-black transition-colors"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                      </button>
                    )}
                  </div>

                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                        Name <span className="text-crimson">*</span>
                      </label>
                      <input
                        type="text"
                        value={author.name}
                        onChange={(e) => handleAuthorChange(index, 'name', e.target.value)}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                        placeholder="Full name"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                        Email <span className="text-crimson">*</span>
                      </label>
                      <input
                        type="email"
                        value={author.email}
                        onChange={(e) => handleAuthorChange(index, 'email', e.target.value)}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                        placeholder="email@example.com"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                        Affiliation <span className="text-crimson">*</span>
                      </label>
                      <input
                        type="text"
                        value={author.affiliation}
                        onChange={(e) => handleAuthorChange(index, 'affiliation', e.target.value)}
                        required
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                        placeholder="University or Organization"
                      />
                    </div>

                    <div>
                      <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                        Role (Optional)
                      </label>
                      <input
                        type="text"
                        value={author.role}
                        onChange={(e) => handleAuthorChange(index, 'role', e.target.value)}
                        className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                        placeholder="e.g., Researcher, Student"
                      />
                    </div>
                  </div>
                </div>
              ))}

              <button
                type="button"
                onClick={addAuthor}
                className="w-full py-3 border-2 border-dashed border-gray-300 rounded-lg text-gray-600 hover:border-crimson hover:text-crimson transition-colors font-outfit font-600 flex items-center justify-center gap-2"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Another Author
              </button>
            </div>
          </div>

          {/* Section 4: Submission Links */}
          <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-sm">
            <h2 className="text-2xl font-outfit font-800 text-black mb-6 flex items-center gap-2">
              <span className="text-crimson">04</span> Submission Links
            </h2>
            
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Paper PDF Link (Google Drive / Dropbox) <span className="text-crimson">*</span>
                </label>
                <input
                  type="url"
                  name="driveLink"
                  value={formData.driveLink}
                  onChange={handleChange}
                  required
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="https://drive.google.com/..."
                />
                <p className="text-sm text-gray-500 mt-1 font-outfit">
                  Please ensure the link has public view access
                </p>
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  GitHub / Project Repository (Optional)
                </label>
                <input
                  type="url"
                  name="githubLink"
                  value={formData.githubLink}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="https://github.com/..."
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Demo / Video Link (Optional)
                </label>
                <input
                  type="url"
                  name="demoLink"
                  value={formData.demoLink}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                  placeholder="https://youtube.com/... or https://..."
                />
              </div>
            </div>
          </div>

          {/* Section 5: Optional Information */}
          <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-sm">
            <h2 className="text-2xl font-outfit font-800 text-black mb-6 flex items-center gap-2">
              <span className="text-crimson">05</span> Additional Information (Optional)
            </h2>
            
            <div className="space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                    Expected Output Type
                  </label>
                  <select
                    name="outputType"
                    value={formData.outputType}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit bg-white"
                  >
                    <option value="">Select type</option>
                    <option value="poster-only">Poster only</option>
                    <option value="poster-demo">Poster + Demo</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                    Preferred Presentation Mode
                  </label>
                  <select
                    name="presentationMode"
                    value={formData.presentationMode}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit bg-white"
                  >
                    <option value="">Select mode</option>
                    <option value="onsite">On-site</option>
                    <option value="poster-only">Poster only</option>
                  </select>
                </div>
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Hardware / Setup Requirements (if any)
                </label>
                <textarea
                  name="setupRequirements"
                  value={formData.setupRequirements}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit resize-none"
                  placeholder="Describe any special hardware or setup needed for demo..."
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Team Bio (2-3 lines)
                </label>
                <textarea
                  name="teamBio"
                  value={formData.teamBio}
                  onChange={handleChange}
                  rows="3"
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit resize-none"
                  placeholder="Brief background about your team..."
                />
              </div>

              <div>
                <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                  Prior Publication Status
                </label>
                <select
                  name="priorPublication"
                  value={formData.priorPublication}
                  onChange={handleChange}
                  className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit bg-white"
                >
                  <option value="">Select status</option>
                  <option value="not-published">Not published</option>
                  <option value="submitted">Submitted elsewhere</option>
                  <option value="published">Published</option>
                </select>
              </div>

              {formData.priorPublication === 'published' && (
                <div>
                  <label className="block text-sm font-outfit font-600 text-gray-700 mb-2">
                    Publication Link
                  </label>
                  <input
                    type="url"
                    name="publicationLink"
                    value={formData.publicationLink}
                    onChange={handleChange}
                    className="w-full px-4 py-3 border-2 border-gray-300 rounded-lg focus:border-crimson focus:outline-none font-outfit"
                    placeholder="https://..."
                  />
                </div>
              )}
            </div>
          </div>

          {/* Section 6: Declarations */}
          <div className="bg-white p-8 rounded-lg border-2 border-gray-200 shadow-sm">
            <h2 className="text-2xl font-outfit font-800 text-black mb-6 flex items-center gap-2">
              <span className="text-crimson">06</span> Declarations
            </h2>
            
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="originalityConfirmed"
                  checked={formData.originalityConfirmed}
                  onChange={handleChange}
                  required
                  className="mt-1 w-5 h-5 border-2 border-gray-300 rounded focus:ring-crimson text-crimson"
                />
                <label className="text-sm font-outfit font-600 text-gray-700">
                  I confirm this work is original and not previously published. <span className="text-crimson">*</span>
                </label>
              </div>

              <div className="flex items-start gap-3">
                <input
                  type="checkbox"
                  name="displayConsent"
                  checked={formData.displayConsent}
                  onChange={handleChange}
                  required
                  className="mt-1 w-5 h-5 border-2 border-gray-300 rounded focus:ring-crimson text-crimson"
                />
                <label className="text-sm font-outfit font-600 text-gray-700">
                  I authorize AI Anytime & event organizers to display this work for review and exhibition. <span className="text-crimson">*</span>
                </label>
              </div>
            </div>
          </div>

          {/* Submit Button */}
          <div className="text-center">
            <button
              type="submit"
              className="btn-primary text-lg px-12 py-4"
            >
              Submit Paper
            </button>
            <p className="text-sm text-gray-500 mt-4 font-outfit">
              All fields marked with <span className="text-crimson">*</span> are required
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default PaperSubmission;
